// Grid inline de productos del usuario — reemplaza al sheet separado
// que había antes. Vive dentro del mismo panel lateral / bottom sheet del
// preset, así no hay un modal encima de otro modal.
//
// Tiene dos variantes:
//   • mobile  → iOS-style bottom sheet: drag handle pill + header con
//               "Cancelar" a la izquierda y título "Mis productos" al
//               centro (mismo patrón que los menús de PruebaNuevaInterfaz
//               y EditarImagen). Toma el panel completo sin padding
//               externo.
//   • desktop → versión simple inline: "Volver" link arriba + título +
//               grid. Vive dentro de la options card del split horizontal.
//
// Si el preset trae `tiposProductoSugeridos` y el rubro es de moda,
// separamos en sección "Sugeridos" (matchean model_view) y "Otros".

import ReactDOM from 'react-dom';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  PackageX,
  PanelLeftOpen,
  Pencil,
  Plus,
  X,
  Sparkles,
} from 'lucide-react';
import { AbanicoDeFotos } from '../../../components/AbanicoDeFotos';
import { TarjetaDeSubidaEnGrilla } from '../../../components/TarjetaDeSubidaEnGrilla';
import {
  useUserProducts,
  useUserProductsLoaded,
  useProductEdit,
} from '../productsContext';
import { getLastUsedProductIds } from '../lastUsedProducts';
import {
  applyProductCatalogFilter,
  DEFAULT_PRODUCT_SORT,
  MIN_PRODUCTS_FOR_TOOLBAR,
  type ProductCatalogSort,
} from '../productCatalogFilter';
import {
  ProductCatalogNoResults,
  ProductCatalogToolbar,
} from './ProductCatalogToolbar';
import CachedImg from '../../../components/CachedImg';
import { useEsMobile } from '../../../hooks/useEsMobile';
import ProductGridSkeleton from '../../../components/ProductGridSkeleton';
import { EditProductFlow } from '../../../components/EditProductFlow';
import type { MockProductV2, PresetV2 } from '../mockData';
// El modo Modelo cuenta sus tres variantes del banco de pruebas (temporal) — si no,
// la bajada de "Subir nuevo producto" les saldría la genérica y no la del modo.
import { esDesignModelo } from '../modesCatalog';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

export interface PickProductPanelProps {
  variant: 'mobile' | 'desktop';
  /** El user confirma su selección (uno o varios productos). */
  onConfirm: (products: MockProductV2[]) => void;
  onBack: () => void;
  /** Si el template tiene tipos sugeridos y el rubro califica, separamos
   *  los productos del user en "Sugeridos" (matchean model_view) y
   *  "Otros productos". */
  tiposProductoSugeridos?: string[];
  rubro?: string | null;
  /** Selección inicial (ids, en orden — el primero es el "principal").
   *  Permite reabrir el picker con lo que ya estaba elegido. */
  initialSelectedIds?: string[];
  /** Si true, el primer seleccionado se marca como "Principal" en su tile
   *  (usado al combinar productos en catálogo). */
  showPrimary?: boolean;
  /** Oculta el link "Volver" del header desktop (cuando el sheet ya tiene
   *  su propio cierre, p.ej. una X). */
  hideBack?: boolean;
  /** Selección ÚNICA: tocar un producto reemplaza al elegido (tocar el mismo
   *  lo deselecciona). Lo usa el modo video-influencer, que genera con un
   *  solo producto (ADR-563). */
  singleSelect?: boolean;
  /** El header (título + subtítulo) va DENTRO del contenedor scrolleable en vez
   *  de fijo arriba, así se va al scrollear y deja el buscador contra el borde.
   *  Lo usa la pantalla full-screen de mobile, donde el alto es poco y tener el
   *  header clavado se come media pantalla. En desktop se deja fijo. */
  scrollHeader?: boolean;
  /** Oculta TODO el header (título + descripción + "Volver"), dejando solo el
   *  buscador y la grilla. Lo usa el sheet de referencias del asistente, que
   *  ya tiene su propio encabezado con pestañas arriba: repetir un título ahí
   *  adentro sumaba ruido y comía alto de pantalla. */
  hideHeader?: boolean;
  /** Oculta el footer con "Confirmar". Lo usa el sheet de referencias del
   *  asistente, que tiene UN solo botón "Utilizar" para todas sus pestañas:
   *  con el footer propio del panel habría dos CTA compitiendo. Requiere
   *  `onSelectionChange` para enterarse de lo elegido. */
  hideFooter?: boolean;
  /** Reporta la selección EN VIVO (no al confirmar). Necesario cuando el
   *  caller oculta el footer y confirma por su cuenta. */
  onSelectionChange?: (products: MockProductV2[]) => void;
  /** Título del header desktop. Default "Elegí los productos". */
  title?: string;
  /** Subtítulo / explicación de qué se está seleccionando. */
  description?: string;
  /** SOLO DESKTOP. Si se provee, el panel muestra ARRIBA del grid (dentro
   *  del scroll) el header "Elegí el producto para generar" + botón "Subir
   *  nuevo producto", fusionando el paso choose-product con la lista. Usado
   *  en los modos de diseño en desktop. En mobile NO se pasa (queda el flujo
   *  full-screen separado). */
  onStartUpload?: (files: FileList | File[]) => void;
  /** Si se provee, el botón "Subir nuevo producto" NO abre el file picker:
   *  navega a la pantalla explicativa (estado vacío de UploadProductPanel).
   *  Tiene prioridad sobre `onStartUpload`. */
  onStartNewProduct?: () => void;
  /** Kind del preset — controla la copy del header de upload (solo cuando
   *  `onStartUpload` está presente). */
  presetKind?: PresetV2['kind'];
  /** SOLO DESKTOP fusionado. Esconde el bloque de arriba del grid: el título "Elegí el
   *  producto para generar" + su bajada, el botón "Subir nuevo producto" y el separador
   *  "o elegí uno tuyo". Para el layout apilado del Catálogo, donde la imagen de muestra
   *  ya explica el modo arriba y el bloque repetía el mensaje ocupando media pantalla.
   *
   *  NO aplica cuando el usuario todavía no tiene productos: ahí ese bloque es la ÚNICA
   *  entrada para subir el primero, y esconderlo deja la pantalla sin salida. */
  ocultarBloqueSubida?: boolean;
  /** Si se provee, la grilla arranca con una tarjeta PLACEHOLDER "Subir nuevo producto"
   *  —mismo tamaño que las de producto, con borde punteado— y tocarla dispara esto (el
   *  host abre el drawer de alta). Es el reemplazo del bloque de subida de arriba del
   *  grid cuando se esconde con `ocultarBloqueSubida`: el alta deja de ser un bloque
   *  fijo que empuja la grilla y pasa a ser la primera opción DE la grilla. */
  onSubirProducto?: () => void;
  /**
   * Con `onSubirProducto`: en vez de UNA tarjeta de subida, DOS —"Subir un producto" y
   * "Subir varios productos"— en una fila propia arriba de la grilla, y ésta es la de varios
   * (`onSubirProducto` queda para la de uno). Las dos abren el pop-up de subir del modo,
   * como en Modelo (ADR-2026-10-16-subir-en-dos-tarjetas-en-todos-los-modos).
   */
  onSubirVarios?: () => void;
  /** Tarjeta sin marco: el nombre deja de vivir en un pie blanco debajo de la foto y
   *  pasa a ir ENCIMA de ella, abajo, en blanco y bold sobre un degradado a negro que le
   *  da contraste. Sin borde gris — el azul de seleccionada se mantiene, que es el único
   *  que informa algo. */
  nombreSobreLaFoto?: boolean;
  /** Fija la cantidad de columnas de la grilla, ignorando el ancho medido. Sin esto son
   *  3 o 2 según el ancho del panel, que es lo que quiere el resto de la app; el Catálogo
   *  las fija para que la tarjeta tenga un tamaño concreto en su columna. Valores
   *  soportados: 2 a 5 (las clases de Tailwind están escritas a mano, ver
   *  `gridColsClass`). */
  columnasFijas?: number;
  /** Slot al PIE del panel, entre la grilla y el footer de "Confirmar". Fijo: no entra
   *  al scroll de la grilla, así los controles que van acá quedan siempre a la vista.
   *  Lo usa el rediseño de "Elegí el producto" para los ajustes rápidos (Calidad,
   *  Fondo, Formato). */
  pie?: React.ReactNode;
  /** El footer se muestra SIEMPRE (con el CTA deshabilitado mientras no haya selección)
   *  en vez de aparecer recién al elegir un producto. Con el CTA a la vista desde el
   *  principio se lee de una qué se espera y cuál es el paso siguiente; sin esto el
   *  footer entra de golpe y corre el contenido hacia arriba. */
  footerSiempreVisible?: boolean;
  /** Texto del CTA. Default "Confirmar". */
  confirmLabel?: string;
  /** Reemplaza la acción del CTA. Sin esto confirma la selección de productos; el paso de
   *  configuración lo usa para que el mismo botón dispare la generación. */
  onConfirmOverride?: () => void;
  /** Nodo a la derecha del texto del CTA — la pastilla de créditos. Reemplaza al contador
   *  de seleccionados. */
  confirmBadge?: React.ReactNode;
  /**
   * Nodo a la IZQUIERDA del CTA, en su misma línea y con caja propia — el contador de
   * imágenes del modo Automático (ADR-2026-08-28-automatico-contador-junto-al-generar).
   *
   * No es un `confirmBadge`: eso vive DENTRO del botón y se lee como información del
   * CTA; esto es un control aparte, que se toca sin disparar la generación. Va acá y no
   * en el `pie` porque es el único ajuste que cambia lo que sale y lo que cuesta, y se
   * decide con el precio del botón a la vista.
   */
  antesDelConfirm?: React.ReactNode;
  /** Línea de aclaración ARRIBA del CTA, en tono bajo. La usa el `configure` a dos
   *  columnas para el resumen de la tanda ("4 imágenes · 2 modelos × 2 poses"): ese
   *  Generar es el de este panel, así que el resumen tiene que vivir acá. */
  confirmHint?: React.ReactNode;
  /**
   * Línea DEBAJO del CTA — lo que falta para poder confirmar ("Elegí al menos un
   * producto").
   *
   * Aparte de `confirmHint`, que va arriba, y no es lo mismo: éste sólo se dibuja
   * mientras falta algo, así que arriba movería el botón bajo el dedo justo cuando el
   * usuario toca un producto. Es la misma decisión —y el mismo tono— que la nota de
   * `BarraInferiorMobile` en el modo Modelo
   * (ADR-2026-08-24-modelo-v2-mobile-barra-en-las-dos-pantallas).
   */
  confirmNota?: React.ReactNode;
  /**
   * Apaga el CTA aunque haya productos elegidos: lo usa el paso 2, donde falta algo de
   * la OTRA columna (sin modelo o sin pose no hay imagen que generar).
   *
   * Va como prop y no se deduce acá porque este panel no sabe nada de esa columna.
   */
  confirmDisabled?: boolean;
  /** Pistas de uso de la grilla, para el rediseño de "Elegí el producto":
   *
   *   • una línea arriba que dice que se puede elegir MÁS DE UNO (y cuántos van), y
   *   • un degradado al pie del scroll mientras quede contenido abajo.
   *
   *  Las dos hacen falta por lo mismo: la pantalla esconde el bloque de título y
   *  descripción que antes explicaba el paso, y la grilla queda cortada por una fila
   *  parcial que se puede leer como el final de la lista. */
  pistasDeUso?: boolean;
  /**
   * Reemplaza el título de la grilla cuando todavía no hay nada elegido.
   *
   * Lo usa el Catálogo, que explica el modo en un párrafo debajo del título de la
   * SECCIÓN: sin esto, la línea de la grilla repetía el mismo mensaje dos veces en la
   * misma columna. Con algo elegido el título pasa a ser el recuento y esta prop no
   * interviene.
   */
  tituloGrilla?: string;
  /**
   * El footer del CTA va SIN la línea que lo separa de la grilla.
   *
   * Con el degradado del pie del scroll (`pistasDeUso`) la línea es un segundo
   * separador para lo mismo, y las dos juntas cortan la grilla dos veces. Lo usa el paso
   * 1 del Catálogo, donde el pie es sólo el botón: sin ajustes en el medio, no hay nada
   * que la línea tenga que delimitar.
   */
  footerSinBorde?: boolean;
  /**
   * El `confirmHint` se lee como TEXTO de la pantalla y no como una nota al pie: negro
   * en vez del gris al 55%, y más aire contra el botón.
   *
   * Los dos usos son distintos. En el paso 2 la línea es un pie del CTA —el resumen de
   * lo que se va a generar, o qué falta—, y ahí el tono bajo es correcto: se lee después
   * del botón. En el paso 1 del Catálogo es lo único que explica el modo, así que tiene
   * que leerse ANTES y no colgar del botón como una aclaración.
   */
  confirmHintDestacado?: boolean;
  /**
   * Tira de PREVIEW de lo elegido, arriba del hint y del CTA: las miniaturas de los
   * productos que ya entraron, seguidas de recuadros punteados vacíos.
   *
   * Los huecos son la mitad que importa. La grilla dice qué está elegido (borde azul +
   * número), pero no dice que la tanda pueda seguir creciendo: los recuadros vacíos al
   * lado de las miniaturas muestran el espacio libre, que es lo que invita a sumar otro.
   * Por eso quedan SIEMPRE —aun con muchos elegidos— y no se agotan.
   */
  tiraDeSeleccion?: boolean;
  /**
   * Lo elegido SALE de la grilla y pasa a vivir en la tira del pie.
   *
   * Requiere `tiraDeSeleccion`: sin la tira, el producto elegido desaparecería y no
   * quedaría en ningún lado. Con las dos, la pantalla se lee como un movimiento —el
   * producto se muda de la grilla a la tanda— en vez de como un tilde, y la grilla queda
   * mostrando SÓLO lo que todavía se puede sumar, que es lo que se está mirando.
   */
  ocultarElegidas?: boolean;
  /**
   * Las tarjetas elegidas muestran su NÚMERO DE ORDEN en vez del check.
   *
   * Es el tratamiento del Catálogo, donde elegir productos es armar una TANDA: ver el
   * 1, el 2 y el 3 acumularse dice "esto es una lista que estás juntando" de una manera
   * que tres checks idénticos no dicen — con el check, la grilla se lee como una serie
   * de decisiones sueltas. El número es además el que después multiplica a las vistas.
   *
   * Con `ocultarElegidas` el número NO llega a verse —la tarjeta sale de la grilla en el
   * mismo frame— y lo que queda rindiendo es el "+" del hover sobre las que todavía se
   * pueden sumar. El badge se conserva porque "numerar sin ocultar" sigue siendo una
   * combinación válida (ADR-2026-08-26-catalogo-la-tarjeta-se-muda-a-la-tira).
   */
  ordenDeSeleccion?: boolean;
  /** Cabecera de la PANTALLA (breadcrumb + título del modo) metida adentro del panel, en
   *  su borde de arriba. La pone el host cuando no la dibuja afuera: así la fila de
   *  columnas arranca pegada al techo y se queda con el alto que antes ocupaba la
   *  cabecera. Va fija, fuera del scroll de la grilla. */
  cabecera?: React.ReactNode;
  /**
   * Modo PREVIEW: el panel se reduce a lo elegido. Se van el buscador, la tarjeta de
   * "Subir nuevo producto" y los productos NO seleccionados; quedan las miniaturas de la
   * selección, los ajustes y el CTA.
   *
   * Es para el paso de configuración, donde el panel deja de ser la herramienta con la
   * que se opera y pasa a ser el recordatorio de con qué se está generando — y le cede el
   * ancho a la columna que sí se está usando. Para cambiar la selección se vuelve al paso
   * anterior por el breadcrumb.
   */
  compacto?: boolean;
  /** Alterna entre la preview y el panel completo, sin salir del paso. Si se provee, la
   *  fila del título muestra el control. */
  onAlternarCompacto?: () => void;
  /** "Generar sin producto" (ADR-673) — solo se renderiza en el header de
   *  subida fusionado (desktop) y solo con `presetKind === 'video-influencer'`. */
  onSkipProduct?: () => void;
  /**
   * Dónde se DIBUJA la tira una vez que hay algo elegido: un nodo del DOM que el host
   * monta en la columna de al lado, en el lugar que ocupaba la muestra del modo
   * (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra).
   *
   * Con la tira vacía la tarjeta sigue colgada del borde derecho del panel —ahí los
   * huecos punteados invitan a empezar—; con el primer producto elegido la tira se muda
   * a este nodo y crece a tablero. El nodo lo monta el host SIEMPRE (vacío no ocupa
   * nada) y no recién al elegir: si apareciera con la primera elección, la tira se
   * dibujaría un frame en su lugar viejo y saltaría, y el vuelo de la tarjeta aterrizaría
   * en la posición equivocada.
   */
  destinoTira?: HTMLElement | null;
  /**
   * Aire (px) al final de la grilla scrolleable de escritorio, para una barra que el host deja
   * FLOTANDO encima del borde de abajo: sin esto, la última fila queda debajo de la barra
   * (ADR-2026-10-14-el-pie-de-elegir-productos-es-el-del-agente). Sin la prop, nada cambia.
   */
  espacioAlPie?: number;
  /**
   * Escritorio: al scrollear la grilla se pliegan la `cabecera` y la fila del recuento y queda
   * arriba sólo el buscador (ADR-2026-10-14-al-scrollear-queda-solo-el-buscador). Es el
   * `scrollHeader` de mobile llevado a la cabecera del host. Sin la prop, nada cambia.
   */
  plegarAlScrollear?: boolean;
}

export const PickProductPanel: React.FC<PickProductPanelProps> = ({
  variant,
  onConfirm,
  onBack,
  tiposProductoSugeridos,
  rubro,
  initialSelectedIds,
  showPrimary = false,
  hideBack = false,
  singleSelect = false,
  scrollHeader = false,
  hideHeader = false,
  hideFooter = false,
  onSelectionChange,
  title = 'Elegí los productos',
  description = 'Podés elegir uno o varios.',
  onStartUpload,
  onStartNewProduct,
  onSkipProduct,
  destinoTira,
  espacioAlPie,
  plegarAlScrollear = false,
  presetKind,
  ocultarBloqueSubida = false,
  onSubirProducto,
  onSubirVarios,
  nombreSobreLaFoto = false,
  columnasFijas,
  pie,
  footerSiempreVisible = false,
  confirmLabel = 'Confirmar',
  onConfirmOverride,
  confirmBadge,
  antesDelConfirm,
  confirmHint,
  confirmNota,
  confirmDisabled = false,
  pistasDeUso = false,
  tituloGrilla,
  footerSinBorde = false,
  confirmHintDestacado = false,
  tiraDeSeleccion = false,
  ocultarElegidas = false,
  ordenDeSeleccion = false,
  cabecera,
  compacto = false,
  onAlternarCompacto,
}) => {
  // File picker oculto para el botón "Subir nuevo producto" (modo fusionado
  // desktop). Solo se usa cuando `onStartUpload` está presente.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadDesc =
    presetKind === 'design-auto'
      ? 'Atlas va a usar esta foto y armar la escena entera con IA — sin configurar nada más.'
      : esDesignModelo(presetKind)
        ? `Atlas va a usar esta foto y combinarla con el ${palabraDeModeloActual().singular}, la pose y el fondo que elijas.`
        : 'Atlas va a usar esta foto para aplicar el preset elegido y armar la imagen final.';

  // Columnas del grid (desktop): 3 cuando el panel queda ancho —a dos columnas, en
  // ese ancho, cada producto ocupa media pantalla (ADR-2026-08-03-anuncio-productos-tres-por-fila)—
  // y 2 cuando no. Lo decide el ANCHO, no el modo (ADR-2026-08-03-las-columnas-las-decide-el-ancho).
  //
  // Medimos el ancho REAL del contenedor scrolleable con un ResizeObserver y no el
  // del viewport: el mismo modo se ve más angosto con la sidebar abierta (en mobile
  // el ref no se monta → queda 2).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [gridCols, setGridCols] = useState(2);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 520px es el ancho a partir del cual 3 columnas dejan tarjetas de ~155px, el
    // mismo tamaño que ya tienen las de plantillas cuando pasan a 3.
    //
    // Es el DEFECTO y ya no una lista de modos —
    // ADR-2026-08-03-las-columnas-las-decide-el-ancho. La lista venía creciendo de a
    // un `kind` por vez (anuncio, después Automática y Modelo, después Catálogo) y
    // cada modo que entraba al mismo layout arrancaba con tarjetas del doble de
    // tamaño hasta que alguien se acordaba de sumarlo. Todos los que muestran la
    // preview chica a la izquierda y este panel a la derecha comparten ancho, así que
    // comparten respuesta; y donde el panel NO es ancho, el `ResizeObserver` lo mide
    // y deja 2 columnas solo.
    //
    // Plantillas es la única excepción y sigue explícita: ahí las 3 columnas son para
    // cuando el panel se vuelve MUY ancho porque la imagen de la plantilla es angosta,
    // que no es la misma pregunta.
    const minimoParaTres = presetKind === 'template' ? 768 : 520;
    const measure = () => {
      medirFinDelScroll(el);
      if (columnasFijas) {
        setGridCols(columnasFijas);
        return;
      }
      // clientWidth incluye el px-6 (48px) de padding del contenedor.
      setGridCols(el.clientWidth >= minimoParaTres ? 3 : 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [presetKind, columnasFijas]);
  // Mapa explícito y no `grid-cols-${n}`: Tailwind escanea el fuente, y una clase armada
  // por interpolación no llega a generarse nunca.
  const gridColsClass =
    gridCols === 5
      ? 'grid-cols-5'
      : gridCols === 4
        ? 'grid-cols-4'
        : gridCols === 3
          ? 'grid-cols-3'
          : gridCols === 1
            ? 'grid-cols-1'
            : 'grid-cols-2';
  // La lista cambia de largo al buscar, al ordenar o al volver del alta de un producto:
  // el degradado del pie tiene que apagarse si ya no quedó nada abajo.
  useEffect(() => {
    medirFinDelScroll(scrollRef.current);
  });
  // Products reales del user — vienen del ProductsContext (poblado por
  // InterfazV2 via fetchUserProducts). Si no hay productos cargados, la
  // grilla queda vacía.
  const rawProducts = useUserProducts();
  // Productos usados en la última generación — llevan un badge "Último
  // utilizado". Leemos los ids una sola vez al montar. Ya NO los flotamos
  // arriba: para eso está el orden explícito "Últimos utilizados" del toolbar
  // (que además cubre TODO el historial, no solo la última generación), y
  // flotarlos hacía que "Últimos subidos" no respetara su propio criterio.
  const lastUsedIds = useMemo(() => getLastUsedProductIds(), []);
  const lastUsedSet = useMemo(() => new Set(lastUsedIds), [lastUsedIds]);
  // Buscador + orden del catálogo. Con búsqueda activa la grilla se aplana
  // (sin secciones Sugeridos / Otros).
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<ProductCatalogSort>(DEFAULT_PRODUCT_SORT);

  // ── Mobile: header colapsable en vez de toolbar sticky ──────────────
  // En mobile el toolbar NO va adentro del contenedor scrolleable: ahí el
  // sticky quedaba anclado unos px por debajo del borde y se veían pasar los
  // bordes de las cards por esa franja (dos intentos de encontrar el offset no
  // dieron con la causa). Va como fila fija, y para que igual "desaparezca" el
  // bloque de título al scrollear —que era el pedido— ese bloque se colapsa por
  // altura. Resultado: el orden visual de siempre (título arriba, buscador
  // debajo) con una barra que no se mueve nunca, sin depender de
  // `position: sticky`.
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  /**
   * ¿El scroll de la grilla tiene contenido por debajo de lo que se ve?
   *
   * Decide si se dibuja el degradado del pie. Se recalcula al scrollear y cuando cambia
   * el contenido o el tamaño del contenedor — si sólo se mirara el scroll, arrancaría
   * apagado hasta el primer movimiento, que es justo cuando hace falta.
   */
  const [quedaContenidoAbajo, setQuedaContenidoAbajo] = useState(false);
  const medirFinDelScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    // 4px de tolerancia: con zoom del browser `scrollTop` queda fraccionario y la resta
    // nunca da exactamente 0 al final.
    setQuedaContenidoAbajo(
      el.scrollHeight - el.scrollTop - el.clientHeight > 4,
    );
  };

  const handleCollapseOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    // Umbrales distintos para colapsar y expandir: sin esa histéresis el header
    // titila cuando el scroll queda justo en el límite.
    setHeaderCollapsed((prev) => (prev ? top > 8 : top > 24));
  };
  const collapsibleClass = (open: boolean) =>
    `shrink-0 overflow-hidden transition-all duration-200 ease-out ${
      open ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
    }`;
  /** Con `plegarAlScrollear`, lo que se pliega al scrollear; sin la prop, el nodo tal cual. */
  const plegable = (nodo: React.ReactNode) =>
    plegarAlScrollear ? <div className={collapsibleClass(!headerCollapsed)}>{nodo}</div> : nodo;
  const {
    items: productosDelCatalogo,
    isSearching,
    noResults,
  } = useMemo(
    () => applyProductCatalogFilter(rawProducts, { query, sort }),
    [rawProducts, query, sort],
  );
  const hasProducts = rawProducts.length > 0;
  // El toolbar solo aparece con un catálogo lo bastante grande: con pocos
  // productos entra todo en pantalla y el buscador es ruido.
  const showToolbar =
    !compacto && rawProducts.length >= MIN_PRODUCTS_FOR_TOOLBAR;
  const productsLoaded = useUserProductsLoaded();
  // Mientras el catálogo carga (sin cache aún) mostramos skeleton en vez de
  // un grid vacío o el CTA de "subir" — todavía no sabemos si tiene productos.
  const loadingProducts = !productsLoaded && !hasProducts;
  // Modo fusionado (desktop) SIN productos: lo único lógico es subir uno, así
  // que mostramos SOLO el CTA de upload — sin grid vacío, sin separador "o
  // elegí uno tuyo" y sin botón Confirmar, centrado para que se vea limpio.
  // Solo una vez que el catálogo cargó (sino durante la carga mostraría el
  // CTA centrado en lugar del skeleton).
  const mergedEmptyUpload = !!onStartUpload && !hasProducts && productsLoaded;
  // Multi-selección por toggle. Mantenemos el ORDEN: el primer elemento es
  // el "principal". Al quitar el principal, el siguiente pasa a serlo; al
  // vaciar todo, el próximo en seleccionarse será el nuevo principal.
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => initialSelectedIds ?? [],
  );

  // ── Editar producto (lápiz por card) ────────────────────────────────
  // Producto en edición → monta la modal "Editar producto" (EditProductFlow →
  // VariantForm), que ya resuelve el responsive (panel lateral desktop /
  // bottom-sheet mobile) y portalea a body. `companyId` + `refreshProducts`
  // vienen del ProductEditContext (provisto por InterfazV2) para no threadear
  // props por todos los call-sites del picker.
  const [editingProduct, setEditingProduct] = useState<MockProductV2 | null>(
    null,
  );
  const { companyId, refreshProducts } = useProductEdit();
  // Si un producto seleccionado desaparece del catálogo (p.ej. se borró desde
  // la modal), lo sacamos de la selección para no dejar un id fantasma en el
  // contador ni en el confirm.
  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => rawProducts.some((p) => p.id === id)),
    );
  }, [rawProducts]);

  /**
   * Lo que la grilla dibuja.
   *
   * Con `ocultarElegidas` lo elegido se va: ya está en la tira del pie, y dejarlo también
   * acá lo mostraba dos veces. `compacto` es la excepción exacta —ese modo es la preview
   * de lo elegido, o sea justo lo contrario— y filtra por su cuenta más abajo.
   */
  const products =
    ocultarElegidas && !compacto
      ? productosDelCatalogo.filter((p) => !selectedIds.includes(p.id))
      : productosDelCatalogo;

  /** Se eligieron TODOS: la grilla queda vacía y hay que decir por qué. */
  const todoElegido =
    ocultarElegidas &&
    !compacto &&
    !loadingProducts &&
    products.length === 0 &&
    productosDelCatalogo.length > 0;

  /**
   * El vuelo de la tarjeta desde la grilla hasta la tira del pie.
   *
   * Con `ocultarElegidas` el producto DESAPARECE de la grilla en el mismo frame en que
   * se elige. Sin animación eso se lee como que la tarjeta se borró, no como que se
   * mudó a la tanda — y la tira de abajo, que es donde reapareció, queda fuera del foco
   * de atención justo cuando tiene algo nuevo que mostrar.
   *
   * Se anima un CLON suelto (`position: fixed`, colgado del body) y no la tarjeta misma:
   * la tarjeta ya no existe en el DOM después del re-render, y la celda de destino vive
   * en otro contenedor con su propio `overflow`. El clon no está sujeto a ninguno de los
   * dos.
   */
  const tiraRef = useRef<HTMLDivElement | null>(null);
  /**
   * La tira, desplegada: muestra TODOS los elegidos en varias filas en vez de resumir
   * en "+N".
   *
   * El "+N" resumía productos que después no había manera de tocar: con `ocultarElegidas`
   * salen de la grilla, así que el único lugar donde existían era esa celda —y la celda
   * no era ningún control—. De la sexta elección en adelante los productos quedaban
   * elegidos y fuera de alcance: no se veían y no se podían sacar
   * (ADR-2026-08-28-catalogo-la-tira-se-despliega).
   */
  const [tiraExpandida, setTiraExpandida] = useState(false);
  const vueloRef = useRef<{
    id: string;
    origen: DOMRect;
    src: string;
  } | null>(null);
  /** Id en vuelo: su celda en la tira va invisible hasta que el clon aterriza. */
  const [enVuelo, setEnVuelo] = useState<string | null>(null);

  useLayoutEffect(() => {
    const vuelo = vueloRef.current;
    if (!vuelo) return;
    vueloRef.current = null;
    // El clon se crea YA, en el origen: la tarjeta desaparece de la grilla en este mismo
    // frame, así que cualquier demora en dibujarlo se ve como un parpadeo.
    const clon = document.createElement('div');
    clon.style.cssText = [
      'position:fixed',
      'left:0',
      'top:0',
      // `transform-origin` en la esquina: con el origen al centro, el `scale` del final
      // corre la caja y el clon aterriza descentrado respecto de la celda.
      'transform-origin:0 0',
      `width:${vuelo.origen.width}px`,
      `height:${vuelo.origen.height}px`,
      'border-radius:12px',
      'overflow:hidden',
      'background:#fff',
      'box-shadow:0 10px 30px rgba(0,0,0,.20)',
      'pointer-events:none',
      'z-index:9999',
      `transform:translate(${vuelo.origen.left}px, ${vuelo.origen.top}px)`,
    ].join(';');
    const img = document.createElement('img');
    img.src = vuelo.src;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover';
    clon.appendChild(img);
    document.body.appendChild(clon);

    let anim: Animation | null = null;
    let destinoEl: HTMLElement | null = null;
    /** Prende la celda AL INSTANTE, sin la transición de opacidad que trae heredada. */
    const mostrarCelda = () => {
      if (!destinoEl) return;
      // La celda hereda `transition: … opacity .15s …` de un estilo de más arriba —su
      // `transition-transform` no gana—, así que un `opacity = '1'` pelado la hace
      // aparecer con un fundido de 150ms desde cero. Eso es lo que se veía como "la
      // imagen desaparece por unos milisegundos" justo antes de quedar fija: no era un
      // frame perdido, era el fade. Se apaga la transición, se cambia la opacidad, se
      // fuerza el reflow para que el navegador tome el valor nuevo SIN interpolar, y
      // recién ahí se devuelve la transición para el resto de la vida de la celda.
      destinoEl.style.transition = 'none';
      destinoEl.style.opacity = '1';
      void destinoEl.offsetHeight;
      destinoEl.style.transition = '';
    };
    /**
     * El aterrizaje: primero SE MUESTRA la celda, después se borra el clon.
     *
     * Al revés —que es como estaba— se veía un parpadeo: `clon.remove()` es síncrono y
     * saca el clon en el acto, pero `setEnVuelo(null)` es un `setState`, así que la celda
     * seguía en `opacity: 0` hasta el repintado siguiente. Ese hueco de un frame es el
     * "desaparece por unos milisegundos" que se reportó.
     */
    const aterrizar = () => {
      mostrarCelda();
      clon.remove();
      setEnVuelo(null);
    };
    /**
     * Recién ACÁ se mide la celda de destino y arranca el vuelo — dos frames después de
     * elegir, no en este mismo commit.
     *
     * La tira puede MUDARSE en la misma elección que dispara el vuelo: con el primer
     * producto se va del borde del panel al tablero de la muestra, y la columna se
     * reacomoda cuando la selección llega hasta arriba (un `setState` del host, que
     * corre después de pintar). Midiendo en el commit, el clon apuntaba a la posición
     * VIEJA y aterrizaba a decenas de píxeles de la celda. Dos `requestAnimationFrame`
     * dejan pasar el pintado y el re-render del host; el clon, mientras tanto, se ve
     * quieto en el origen.
     */
    const arrancar = () => {
      destinoEl =
        tiraRef.current?.querySelector<HTMLElement>(
          `[data-tira-tile="${vuelo.id}"]`,
        ) ??
        // El producto entró pero la tira ya resume en "+N": el vuelo aterriza ahí, que es
        // la celda que acaba de cambiar.
        tiraRef.current?.querySelector<HTMLElement>('[data-tira-resumen]') ??
        null;
      if (!destinoEl) {
        clon.remove();
        setEnVuelo(null);
        return;
      }
      // Con la tira desplegada la celda de destino puede estar fuera del scroll: se la
      // trae a la vista ANTES de medirla, si no el clon aterriza en una posición que ya
      // no existe (o directamente fuera de la caja visible).
      destinoEl.scrollIntoView({ block: 'nearest' });
      const destino = destinoEl.getBoundingClientRect();
      const escala = destino.width / vuelo.origen.width;
      anim = clon.animate(
        [
          {
            transform: `translate(${vuelo.origen.left}px, ${vuelo.origen.top}px) scale(1)`,
          },
          {
            transform: `translate(${destino.left}px, ${destino.top}px) scale(${escala})`,
          },
        ],
        { duration: 380, easing: 'cubic-bezier(.2,.7,.25,1)', fill: 'forwards' },
      );
      anim.onfinish = aterrizar;
      // Si la animación no puede correr (navegador sin WAAPI, pestaña en background), la
      // celda tiene que aparecer igual: sin esto quedaría invisible para siempre.
      anim.oncancel = aterrizar;
    };
    let segundoFrame = 0;
    const primerFrame = requestAnimationFrame(() => {
      segundoFrame = requestAnimationFrame(arrancar);
    });
    /**
     * Cierre del vuelo: si el panel se desmonta —o si se elige OTRO producto mientras
     * éste todavía viaja— el clon quedaría colgado del `body` para siempre; no lo borra
     * ningún re-render de React porque nunca fue suyo.
     *
     * Los handlers se desconectan ANTES de cancelar, y acá no se toca el state: sin eso,
     * `anim.cancel()` dispararía `oncancel` → `aterrizar` → `setEnVuelo(null)`, que en el
     * caso de "eligió otro mientras volaba" pisa el vuelo recién empezado. Prender la
     * celda y borrar el clon a mano hace lo mismo que un aterrizaje instantáneo, sin
     * pasar por React.
     */
    return () => {
      cancelAnimationFrame(primerFrame);
      cancelAnimationFrame(segundoFrame);
      if (anim) {
        anim.onfinish = null;
        anim.oncancel = null;
        anim.cancel();
      }
      mostrarCelda();
      clon.remove();
    };
  }, [enVuelo]);

  const toggle = (id: string, desdeLaGrilla?: HTMLElement) => {
    const seSuma = !selectedIds.includes(id);
    // El vuelo se arma ANTES del setState: después, la tarjeta ya no está en el DOM y su
    // posición de origen es imposible de leer.
    if (
      seSuma &&
      ocultarElegidas &&
      desdeLaGrilla &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      const foto = desdeLaGrilla.querySelector('img');
      if (foto) {
        vueloRef.current = {
          id,
          origen: desdeLaGrilla.getBoundingClientRect(),
          src: (foto as HTMLImageElement).currentSrc || (foto as HTMLImageElement).src,
        };
        setEnVuelo(id);
      }
    }
    setSelectedIds((prev) => {
      // Selección única (video-influencer): tocar otro producto REEMPLAZA al
      // elegido; tocar el mismo lo deselecciona (ADR-563).
      if (singleSelect) return prev.includes(id) ? [] : [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };
  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    // Devolvemos en el ORDEN de selección (no el orden del catálogo) para
    // preservar cuál es el principal (primer elemento). El mapa se arma sobre
    // `rawProducts`, NO sobre la lista filtrada: si el user selecciona algo y
    // después busca otra cosa, lo ya elegido sigue seleccionado aunque no esté
    // visible — armarlo con `products` lo dejaría afuera del confirm.
    const byId = new Map(rawProducts.map((p) => [p.id, p]));
    onConfirm(
      selectedIds.map((id) => byId.get(id)).filter(Boolean) as MockProductV2[],
    );
  };
  const hasSelection = selectedIds.length > 0;

  /**
   * La tira de preview del pie: miniaturas de lo elegido + recuadros vacíos.
   *
   * Cuántas miniaturas entran antes de resumir en "+N", y cuántos huecos quedan siempre
   * a la derecha. El mínimo de huecos NO baja de 2: son los que dicen "acá puede entrar
   * más", y son lo único que lo dice una vez que la tira ya tiene miniaturas.
   */
  const TIRA_CELDAS = 6;
  /**
   * En el TABLERO —la tira mudada al lugar de la muestra— entran NUEVE: 3×3.
   *
   * Colgada del panel la tira es una columna angosta y seis celdas ya le llenan el alto;
   * en la muestra hay lugar para una grilla, y con seis quedaba media caja vacía. Nueve
   * es además la cantidad que hace que el "+N" aparezca cuando la tanda es realmente
   * grande, no a la séptima elección.
   */
  const TIRA_CELDAS_TABLERO = 9;
  /** Teléfono de verdad (<640px), que es otra cosa que la prop `variant`. */
  const esMobileReal = useEsMobile();
  /**
   * Arma la tira para un contenedor de `celdas` celdas: qué miniaturas se ven, cuántas
   * resume el "+N" y cuántos huecos punteados quedan.
   *
   * Es una FUNCIÓN de la cantidad de celdas porque los tres contenedores no tienen el
   * mismo tamaño: la fila del pie y la columna del costado entran seis, el tablero de la
   * muestra entra nueve.
   */
  const armarTira = React.useCallback(
    (celdas: number) => {
      if (!tiraDeSeleccion) return null;
      // Sobre `rawProducts` y no sobre la lista filtrada, por lo mismo que
      // `handleConfirm`: lo elegido sigue en la tanda aunque el buscador lo esté
      // ocultando.
      const byId = new Map(rawProducts.map((p) => [p.id, p]));
      const elegidos = selectedIds
        .map((id) => byId.get(id))
        .filter(Boolean) as MockProductV2[];
      // Desplegada, la tira muestra TODO: es el único lugar donde viven los productos
      // elegidos (salieron de la grilla), así que tiene que poder mostrarlos a todos para
      // que se puedan sacar. Plegada vuelve al resumen, que es la forma que no le come el
      // alto a la grilla.
      const desbordada = elegidos.length > celdas;
      const hayResumen = desbordada && !tiraExpandida;
      // Con resumen, la última celda se la queda el "+N": si no, mostraría una miniatura
      // más y el número mentiría por uno.
      const visibles = hayResumen ? elegidos.slice(0, celdas - 1) : elegidos;
      // Desplegada, la última celda se la queda el control para volver a plegar.
      const celdasOcupadas = visibles.length + (desbordada ? 1 : 0);
      return {
        visibles,
        expandida: desbordada && tiraExpandida,
        resto: hayResumen ? elegidos.length - visibles.length : 0,
        // Los huecos son las celdas que TODAVÍA no se llenaron, y se consumen a medida
        // que entran productos. Antes quedaban siempre dos —la idea era que el espacio
        // libre no se agotara nunca—, pero con la tira llena esos dos punteados no se
        // leían como "puede entrar más": se leían como dos celdas rotas que no
        // terminaban de cargar (ADR-2026-08-26-catalogo-los-huecos-se-llenan).
        // Desplegada la tira son varias filas: los huecos son los que faltan para
        // completar la última, así que la grilla no queda con una fila coja.
        huecos:
          desbordada && tiraExpandida
            ? (celdas - (celdasOcupadas % celdas)) % celdas
            : celdas - celdasOcupadas,
      };
    },
    [tiraDeSeleccion, rawProducts, selectedIds, tiraExpandida],
  );
  /** La tira de seis: la fila del pie en mobile y la columna colgada del panel. */
  const tira = React.useMemo(
    () => armarTira(TIRA_CELDAS),
    [armarTira],
  );
  /** La misma tanda, contada para las nueve celdas del tablero de la muestra. */
  const tiraTablero = React.useMemo(
    () => armarTira(TIRA_CELDAS_TABLERO),
    [armarTira],
  );

  /**
   * En desktop la tira NO va al pie: va al tablero de la muestra, en la columna de la
   * derecha — ADR-2026-09-07-la-tira-vacia-no-se-dibuja.
   *
   * Sólo en desktop DE VERDAD: `variant` es un layout, no un dispositivo —las pantallas
   * full-screen de mobile montan este panel con `variant="desktop"` para tener su header
   * y su footer—, así que mirando sólo esa prop el teléfono se quedaba sin la fila del
   * pie, que ahí es la única tira que hay
   * (ADR-2026-09-04-en-mobile-el-panel-no-dibuja-la-tira-de-elegidos).
   */
  const tiraALaDerecha = !!tira && variant === 'desktop' && !esMobileReal;

  /**
   * La tira aparece en el tablero de la muestra con el primer producto elegido
   * (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra).
   *
   * VACÍA no se dibuja en ningún lado (ADR-2026-09-07-la-tira-vacia-no-se-dibuja): antes
   * esperaba colgada del borde derecho del panel, como una columna flotante de seis
   * recuadros punteados. La muestra ya explica el modo, y esos recuadros vacíos al lado
   * competían con ella sin decir nada que la grilla no dijera.
   */
  const tiraEnLaMuestra =
    tiraALaDerecha && !!destinoTira && selectedIds.length > 0;

  /**
   * La tira vuelve al PIE del panel cuando NO hay tablero al que mudarse —
   * ADR-2026-09-08-la-columna-de-resultados-no-vuelve-a-la-portada.
   *
   * El tablero lo monta la muestra del modo, y desde que la columna de al lado se queda
   * con los resultados de la sesión esa muestra puede no existir. Sin este respaldo,
   * elegir un producto lo SACABA de la grilla y no lo dibujaba en ningún lado: el panel
   * decía "1 producto elegido" y no había forma de ver cuál ni de quitarlo.
   *
   * Es la misma fila de seis celdas del teléfono, que es donde vivía la tira antes de
   * mudarse a la muestra (ADR-2026-09-04-la-tira-de-elegidos-va-a-la-derecha). Pide
   * `selectedIds.length > 0` para no reponer la tira vacía
   * (ADR-2026-09-07-la-tira-vacia-no-se-dibuja).
   */
  const tiraAlPieDeRespaldo =
    tiraALaDerecha && !destinoTira && selectedIds.length > 0;

  /**
   * Una celda de la tira. La MISMA en las dos orientaciones: lo único que cambia es el
   * contenedor (fila de 6 columnas al pie, o columna a la derecha).
   *
   * La celda ENTERA quita, no sólo la cruz: es un objetivo de 48px contra uno de 16, y no
   * hay otra acción posible sobre la miniatura con la que confundirse. La cruz se queda
   * como señal de que se puede —sin ella, la miniatura no se lee como algo que se pueda
   * tocar—.
   */
  const celdaTira = (p: MockProductV2) => (
    <button
      key={p.id}
      type="button"
      data-tira-tile={p.id}
      onClick={() => toggle(p.id)}
      title={`Quitar ${p.name}`}
      aria-label={`Quitar ${p.name} de la tanda`}
      style={{ opacity: enVuelo === p.id ? 0 : undefined }}
      className="group/tira relative w-full aspect-square rounded-xl overflow-hidden bg-white ring-1 ring-black/[0.06] block active:scale-95 transition-transform"
    >
      <CachedImg
        src={p.imageUrl}
        alt={p.name}
        className="w-full h-full object-cover"
      />
      {/* Velo al hover: sin él la cruz cae sobre una foto cualquiera y en un packshot
          claro se pierde. */}
      <span className="absolute inset-0 bg-black/0 group-hover/tira:bg-black/35 transition-colors" />
      <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-brand-black/70 group-hover/tira:bg-brand-black grid place-items-center transition-colors">
        <X className="w-3 h-3 text-white" strokeWidth={3} />
      </span>
    </button>
  );

  /** Un recuadro punteado: "acá puede entrar otro". */
  const huecoTira = (key: string) => (
    <span
      key={key}
      aria-hidden="true"
      className="w-full aspect-square rounded-xl border-[1.5px] border-dashed border-black/15 bg-black/[0.02]"
    />
  );

  /**
   * El CONTENIDO de la tira, en orden: las miniaturas elegidas, el control de
   * "+N · Ver todo" (o el "Ver menos" cuando ya está desplegada) y los huecos punteados.
   *
   * Es lo MISMO en las tres orientaciones —la fila al pie en mobile, la columna colgada
   * del borde del panel y el tablero que ocupa la muestra—: lo único que cambia es el
   * contenedor. Lo comparten para que un cambio en la mecánica de la tanda (el "+N", los
   * huecos) no haya que escribirlo tres veces y se desincronice en la primera edición.
   *
   * `chevronAbajo`: hacia dónde crece el contenedor. La fila al pie crece hacia ARRIBA
   * (chevron arriba para desplegar), la columna y el tablero hacia abajo.
   */
  const contenidoTira = (opts: {
    tira: ReturnType<typeof armarTira>;
    chevronAbajo: boolean;
    conHuecos: boolean;
    prefijoKey: string;
  }) => {
    const tira = opts.tira;
    if (!tira) return null;
    const ChevronDesplegar = opts.chevronAbajo ? ChevronDown : ChevronUp;
    const ChevronPlegar = opts.chevronAbajo ? ChevronUp : ChevronDown;
    return (
      <>
        {tira.visibles.map(celdaTira)}
        {/* El "+N" es un BOTÓN: despliega la tira y muestra los que resume. Como celda
            muda dejaba productos elegidos que no se veían ni se podían sacar —con
            `ocultarElegidas` tampoco están en la grilla—, que es el agujero que cerró
            ADR-2026-08-28-catalogo-la-tira-se-despliega. Desplegada, la misma celda
            pliega: el control de ida y el de vuelta son el mismo lugar. */}
        {tira.resto > 0 && (
          <button
            type="button"
            data-tira-resumen
            onClick={() => setTiraExpandida(true)}
            title="Ver todos los productos elegidos"
            aria-label={`Ver los ${tira.resto} productos restantes de la tanda`}
            aria-expanded={false}
            className="w-full aspect-square rounded-xl bg-black/[0.05] hover:bg-black/[0.09] flex flex-col items-center justify-center gap-1.5 text-brand-black/55 transition-colors active:scale-95"
          >
            <span className="text-[13px] font-semibold leading-none">
              +{tira.resto}
            </span>
            <span className="flex items-center gap-0.5 text-[9.5px] font-medium leading-none">
              Ver todo
              <ChevronDesplegar className="w-2.5 h-2.5" strokeWidth={2.5} />
            </span>
          </button>
        )}
        {tira.expandida && (
          <button
            type="button"
            data-tira-resumen
            onClick={() => setTiraExpandida(false)}
            title="Ver menos"
            aria-label="Plegar la tira de productos elegidos"
            aria-expanded
            className="w-full aspect-square rounded-xl bg-black/[0.05] hover:bg-black/[0.09] flex flex-col items-center justify-center gap-0.5 text-brand-black/55 shrink-0 transition-colors active:scale-95"
          >
            <ChevronPlegar className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="text-[9.5px] font-medium leading-none">
              Ver menos
            </span>
          </button>
        )}
        {/* Los huecos son las celdas que TODAVÍA no se llenaron: son lo único que dice
            "acá puede entrar más" una vez que la tira tiene miniaturas. Desplegada no
            van —ahí ya se muestra la tanda entera—. */}
        {opts.conHuecos &&
          !tira.expandida &&
          Array.from({ length: tira.huecos }, (_, i) =>
            huecoTira(`${opts.prefijoKey}-hueco-${i}`),
          )}
      </>
    );
  };

  // Al bajar de la sexta elección ya no hay nada que desplegar: la tira entra entera en
  // una fila. Se pliega sola para que el próximo desborde no aparezca ya abierto.
  useEffect(() => {
    // El umbral es el del contenedor donde la tira está viviendo: en el tablero entran
    // nueve, así que con siete elegidos ya no hay nada que desplegar y con seis sí lo
    // habría si la tira siguiera colgada del panel.
    const celdas = tiraEnLaMuestra ? TIRA_CELDAS_TABLERO : TIRA_CELDAS;
    if (tiraExpandida && selectedIds.length <= celdas) setTiraExpandida(false);
  }, [tiraExpandida, selectedIds.length, tiraEnLaMuestra]);

  // Reporte de selección EN VIVO para quien oculta el footer y confirma por su
  // cuenta. Se mapea sobre `rawProducts` (no la lista filtrada) por lo mismo
  // que `handleConfirm`: lo ya elegido sigue contando aunque el buscador lo
  // esté ocultando.
  useEffect(() => {
    if (!onSelectionChange) return;
    const byId = new Map(rawProducts.map((p) => [p.id, p]));
    onSelectionChange(
      selectedIds.map((id) => byId.get(id)).filter(Boolean) as MockProductV2[],
    );
    // `onSelectionChange` queda fuera de las deps a propósito: los callers la
    // pasan inline (identidad nueva en cada render) y meterla dispara un loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, rawProducts]);

  // ── Sugerencias por tipo de producto ────────────────────────────────
  // Solo para rubros de moda — sería ruidoso aplicarlo a otros rubros
  // donde model_view casi no se completa. Si el template no tiene tipos
  // sugeridos o no hay matches, mostramos un único grid sin sección.
  const SUGGEST_RUBROS = new Set(['Moda e Indumentaria']);
  const shouldSuggest =
    !!rubro &&
    SUGGEST_RUBROS.has(rubro) &&
    Array.isArray(tiposProductoSugeridos) &&
    tiposProductoSugeridos.length > 0;
  const suggestedSet = shouldSuggest
    ? new Set(
        (tiposProductoSugeridos as string[]).map((s) =>
          s.trim().toLowerCase(),
        ),
      )
    : null;
  const { suggested, others } = (() => {
    // Buscando → una sola lista de resultados: partirla en "Sugeridos" y
    // "Otros" estorba cuando lo que querés es un producto puntual.
    if (!suggestedSet || isSearching) {
      return { suggested: [] as MockProductV2[], others: products };
    }
    const sug: MockProductV2[] = [];
    const oth: MockProductV2[] = [];
    for (const p of products) {
      const mv = (p.modelView || '').trim().toLowerCase();
      if (mv && suggestedSet.has(mv)) {
        sug.push(p);
      } else {
        oth.push(p);
      }
    }
    return { suggested: sug, others: oth };
  })();
  const hasSuggestions = suggested.length > 0;

  // Tile reutilizado en ambos viewports. Rounded varia (xl mobile · 2xl
  // desktop) para mantener el look existente.
  /**
   * Tarjeta PLACEHOLDER de "Subir nuevo producto" — primera de la grilla.
   *
   * Espeja la geometría de `renderTile` (foto cuadrada, borde de 2px y el mismo pie —o
   * la falta de pie, con `nombreSobreLaFoto`) para que caiga en la cuadrícula sin
   * desalinear la fila: si midiera distinto, estiraría toda su fila.
   */
  // Las dos formas de subir en una fila PROPIA, arriba de la grilla y repartiéndose el
  // ancho, igual que en Modelo (ADR-2026-10-14-tarjetas-de-subida-a-todo-el-ancho). Con
  // tres o más columnas, apaisadas: la fila mide lo mismo que una de productos.
  const dosFormasDeSubir = !!(onSubirVarios && onSubirProducto);
  const filaDeSubida = dosFormasDeSubir ? (
    <div className="grid grid-cols-2 gap-3 pb-3">
      {(['uno', 'varios'] as const).map((variante) => (
        <TarjetaDeSubidaEnGrilla
          key={variante}
          variante={variante}
          onClick={variante === 'uno' ? onSubirProducto! : onSubirVarios!}
          aspecto={gridCols >= 3 ? 'aspect-[3/2]' : 'aspect-square'}
          redondeo="rounded-2xl"
        />
      ))}
    </div>
  ) : null;

  const renderTileSubir = (radius: 'xl' | '2xl') => {
    const rounded = radius === 'xl' ? 'rounded-xl' : 'rounded-2xl';
    // Con las dos formas de subir, las tarjetas van en su propia fila (`filaDeSubida`),
    // no como celda de la grilla.
    if (dosFormasDeSubir) return null;
    // La tarjeta de subir de Mi marca: punteado celeste, degradé y el abanico de fotos
    // (`AbanicoDeFotos`), que se abre en hover —
    // ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. El borde sigue
    // siendo de 2px para medir lo mismo que las tarjetas de producto.
    return (
      <button
        key="__subir-producto__"
        type="button"
        onClick={onSubirProducto}
        className={`group relative text-left ${rounded} overflow-hidden border-2 border-dashed border-[#C9D3FF] bg-gradient-to-b from-white to-[#F1F4FF] transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary`}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        />
        {/* Con el nombre sobre la foto las tarjetas de producto pierden el pie y quedan
            cuadradas: acá el texto también entra al cuadrado, sino esta tarjeta sería
            más alta que las otras y estiraría la primera fila (y con ella el recorte de
            una fila y media, que se calcula sobre el alto de fila). */}
        {nombreSobreLaFoto ? (
          <div className="relative aspect-square flex flex-col items-center justify-center gap-3 px-2">
            <AbanicoDeFotos />
            <p className="text-[12.5px] font-semibold text-[#16181D] text-center leading-tight">
              Subir nuevo producto
            </p>
          </div>
        ) : (
          <>
            <div className="relative aspect-square flex items-center justify-center">
              <AbanicoDeFotos />
            </div>
            <div className="relative px-3 py-2.5">
              <p className="text-[13.5px] font-semibold text-[#16181D] truncate">
                Subir nuevo producto
              </p>
            </div>
          </>
        )}
      </button>
    );
  };

  const renderTile = (p: MockProductV2, radius: 'xl' | '2xl') => {
    const isSel = selectedIds.includes(p.id);
    /** Posición en la tanda (1-based). Sólo se dibuja con `ordenDeSeleccion`. */
    const orden = selectedIds.indexOf(p.id) + 1;
    const isPrimary = showPrimary && isSel && selectedIds[0] === p.id;
    const isLastUsed = lastUsedSet.has(p.id);
    const rounded = radius === 'xl' ? 'rounded-xl' : 'rounded-2xl';
    return (
      // Card = div con role=button (NO <button>): así el lápiz de editar puede
      // ser un <button> real adentro sin anidar button-en-button (HTML inválido).
      <div
        key={p.id}
        role="button"
        tabIndex={0}
        // `currentTarget` = la tarjeta entera, que es la caja desde la que sale el vuelo
        // hacia la tira del pie.
        onClick={(e) => toggle(p.id, e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle(p.id, e.currentTarget);
          }
        }}
        // `group`: el radio vacío se convierte en un "+" al pasar el mouse por la
        // tarjeta entera, no sólo por el radio — que mide 20px y nadie apunta.
        className={`group text-left ${rounded} overflow-hidden bg-white cursor-pointer ${
          // 1.5px en las tarjetas del rediseño y 2px en las clásicas. El grosor va junto
          // con el color de abajo: los dos estados —gris al 10% y azul de seleccionada—
          // tienen que medir lo MISMO, sino la tarjeta cambia de tamaño al elegirla.
          nombreSobreLaFoto ? 'border-[1.5px]' : 'border-2'
        } ${
          isSel
            ? 'border-brand-primary'
            : nombreSobreLaFoto
              ? // El borde vive ACÁ y no en la foto de adentro: la tarjeta es la que
                // tiene el `rounded` y el `overflow-hidden`, así que un borde en el hijo
                // cuadrado se ve CORTADO en las cuatro esquinas —la curva lo clipea—.
                // Acá sigue la curva y cierra.
                //
                // Ocupa el mismo lugar que el azul de seleccionada, así que la tarjeta
                // no cambia de tamaño al elegirla.
                'border-black/10 hover:border-brand-primary/40'
              : radius === '2xl'
                ? 'border-neutral-200 hover:border-brand-primary/40'
                : 'border-neutral-200'
        } active:scale-[0.98] transition-all relative`}
      >
        <div className="relative aspect-square bg-neutral-100 overflow-hidden">
          {/* `object-cover` = el modo "Fill" de Figma: la foto se escala hasta tapar la
              caja entera y lo que sobra de un lado se recorta. Sin barras y sin deformar,
              que es lo que se pidió; el precio es el recorte —una foto vertical pierde
              arriba y abajo, una apaisada los costados—.

              Se probó la variante "foto entera + copia borrosa de fondo" (object-contain
              sobre la misma imagen ampliada y desenfocada): mostraba el producto completo,
              pero la foto quedaba flotando más chica y el fondo se leía como si la imagen
              estuviera cortada a los costados. Volvió a Fill a pedido. */}
          <CachedImg
            src={p.imageUrl}
            alt={p.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {isLastUsed && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-brand-black text-white text-[10px] font-semibold shadow">
              Último utilizado
            </span>
          )}
          {isSel ? (
            <div className="absolute top-2 right-2 flex items-center gap-1.5">
              {isPrimary && (
                <span className="px-2 py-0.5 rounded-full bg-brand-primary text-white text-[10.5px] font-semibold shadow">
                  Principal
                </span>
              )}
              {/* 20px, el mismo tamaño que el radio vacío de la tarjeta sin elegir: si
                  midieran distinto, el control saltaría de tamaño al seleccionar. El
                  NÚMERO ocupa el lugar del check —no se suma a él— por lo mismo. */}
              <div className="w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center shadow">
                {ordenDeSeleccion ? (
                  <span className="text-[11px] font-bold leading-none text-white tabular-nums">
                    {orden}
                  </span>
                ) : (
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                )}
              </div>
            </div>
          ) : (
            // Radio VACÍO en las no elegidas: es lo que dice, sin tocar nada, que cada
            // tarjeta se prende y se apaga por su cuenta. Con el check apareciendo sólo
            // al seleccionar, la grilla se leía como "elegí una" hasta que probabas.
            //
            // Va en `singleSelect` no: ahí elegir una APAGA la anterior, y una fila de
            // radios vacíos prometería lo contrario.
            //
            // El borde blanco + el fondo negro translúcido lo sostienen sobre cualquier
            // foto: sin eso desaparece contra un packshot en fondo blanco.
            nombreSobreLaFoto &&
            !singleSelect && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full border-[1.5px] border-white/90 bg-black/15 shadow-sm grid place-items-center">
                {/* El "+" aparece al hover: el radio dice que la tarjeta se prende y se
                    apaga, pero no que se SUMA a las anteriores. Sólo con
                    `ordenDeSeleccion` —donde lo que se arma es una tanda—; en los modos
                    que generan una imagen sola, "sumar" prometería otra cosa. */}
                {ordenDeSeleccion && (
                  <Plus
                    className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    strokeWidth={3}
                  />
                )}
              </div>
            )
          )}
          {/* Nombre SOBRE la foto. El degradado arranca transparente arriba y llega a
              negro abajo: es lo que hace legible el texto blanco sobre una foto de
              contenido impredecible (un packshot en fondo blanco lo dejaría invisible).
              `pt-8` para que el degradado tenga recorrido y no corte de golpe. */}
          {/* Sin el lápiz de editar: en esta tarjeta el nombre va sobre la foto, y el
              botón quedaba encima de la imagen tapándola y compitiendo con el tap de
              seleccionar, que es lo único que se hace acá. Editar el producto sigue
              estando en la tarjeta clásica (`nombreSobreLaFoto` apagado) y en Mi marca. */}
          {nombreSobreLaFoto && (
            <div className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
              <p className="text-[11.5px] font-bold text-white truncate">
                {p.name}
              </p>
            </div>
          )}
        </div>
        <div className={nombreSobreLaFoto ? 'hidden' : 'px-3 py-2.5'}>
          {/* Nombre + lápiz de editar en la MISMA fila (estilo Estudio Virtual):
              lápiz plano gris a la derecha. Visible solo cuando la card está
              seleccionada; si no, transparente + sin eventos (pero reserva su
              lugar, así el nombre no se corre al seleccionar). stopPropagation
              para no togglear la selección al tocarlo. */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13.5px] font-medium text-brand-black/90 truncate">
              {p.name}
            </p>
            <button
              type="button"
              tabIndex={isSel ? 0 : -1}
              title="Editar producto"
              aria-label="Editar producto"
              onClick={(e) => {
                e.stopPropagation();
                setEditingProduct(p);
              }}
              className={`p-1.5 rounded-md transition-colors shrink-0 ${
                isSel
                  ? 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100'
                  : 'text-transparent pointer-events-none'
              }`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal "Editar producto" — reusa EditProductFlow (mismo que Mi Marca /
  // Estudio Virtual): VariantForm portalea a body (z-[60]+), así que queda por
  // encima del sheet del picker sin recortarse. onSaved refresca el catálogo del
  // context; onClose cierra. Requiere companyId (del ProductEditContext).
  const editModal =
    editingProduct && companyId ? (
      <EditProductFlow
        companyId={companyId}
        product={{ id: editingProduct.id, name: editingProduct.name }}
        onClose={() => setEditingProduct(null)}
        onSaved={refreshProducts}
      />
    ) : null;

  if (variant === 'mobile') {
    return (
      <>
        {/* Drag handle — pill gris centrada arriba. */}
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* iOS header: Cancelar (azul, izq) · título centrado · Confirmar (der). */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Mis productos
          </h3>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            className="text-[15px] text-brand-primary font-semibold disabled:opacity-40 active:opacity-50 transition-opacity w-20 text-right"
          >
            {selectedIds.length > 0
              ? `Listo (${selectedIds.length})`
              : 'Listo'}
          </button>
        </div>
        {/* Subtítulo colapsable + buscador debajo, ambos AFUERA del scroll —
            mismo patrón que la pantalla full-screen de mobile (ver el comentario
            de `headerCollapsed`). */}
        <div
          className={`px-4 ${collapsibleClass(
            !(showToolbar && !loadingProducts && headerCollapsed),
          )}`}
        >
          <p className="text-[12.5px] text-[#3C3C43]/55 pb-4">{description}</p>
        </div>
        {showToolbar && !loadingProducts && (
          <ProductCatalogToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            className="shrink-0 px-4 pt-1"
            inputId="v2-pick-product-search-mobile"
          />
        )}
        <div
          onScroll={handleCollapseOnScroll}
          className="flex-1 overflow-y-auto px-4 pb-6"
        >
          {loadingProducts ? (
            <ProductGridSkeleton
              count={6}
              gridClassName="grid grid-cols-2 gap-3"
              tileClassName="rounded-xl"
              withLabel
            />
          ) : noResults ? (
            <ProductCatalogNoResults query={query} onClear={() => setQuery('')} />
          ) : hasSuggestions ? (
            <>
              <div className="flex items-center gap-1.5 mb-2 mt-1">
                <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                  Sugeridos para esta plantilla
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {suggested.map((p) => renderTile(p, 'xl'))}
              </div>
              {others.length > 0 && (
                <>
                  <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                    Otros productos
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {others.map((p) => renderTile(p, 'xl'))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => renderTile(p, 'xl'))}
            </div>
          )}
        </div>
        {editModal}
      </>
    );
  }

  // Desktop variant — layout self-contained:
  //   • Header (Volver + título + subtítulo) — shrink-0, o dentro del scroll
  //     si `scrollHeader` (pantalla full-screen de mobile)
  //   • Grilla scrolleable — flex-1 overflow-y-auto
  //   • Footer con Confirmar — shrink-0, border-top
  // Importante: el aside padre NO debe envolver esto en otro overflow-y
  // (ver la rama `step === 'pick-product'` del PresetDetailSheet desktop).

  // Header "Elegí los productos" — solo cuando NO está fusionado con el upload
  // (ej. plantillas). En modo fusionado (modos de diseño desktop) el header
  // "Elegí el producto" + botón Subir van DENTRO del scroll, abajo.
  // El padding horizontal lo pone el contenedor cuando va adentro del scroll,
  // que ya tiene `px-6`.
  // Buscador + orden. En mobile se renderiza afuera del scroll (ver el bloque
  // de `headerCollapsed`); en desktop va adentro, sticky, debajo del bloque de
  // subida.
  const toolbar =
    showToolbar && !loadingProducts && !mergedEmptyUpload ? (
      <ProductCatalogToolbar
        query={query}
        onQueryChange={setQuery}
        sort={sort}
        onSortChange={setSort}
        // Fuera del scroll pone su propio padding horizontal; adentro compensa
        // el `px-6` del contenedor para que el fondo blanco llegue a los bordes.
        className={scrollHeader ? 'shrink-0 px-6 pt-4' : '-mx-6 px-6 pt-4'}
        inputId="v2-pick-product-search-desktop"
      />
    ) : null;

  // `hideHeader` anula el bloque entero (título + descripción + Volver). Los
  // dos sitios de render de abajo ya están guardados por `headerInner &&`, así
  // que con devolver null alcanza y no queda padding fantasma.
  const headerInner = hideHeader ? null : !onStartUpload ? (
    <div className="pt-6 pb-5">
      {!hideBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-brand-primary text-[13px] mb-3 hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Volver
        </button>
      )}
      <p className="text-[16px] font-semibold text-brand-black/90">{title}</p>
      <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">{description}</p>
    </div>
  ) : null;

  // La fila del recuento ("4 productos" / "2 productos elegidos — tocá otros…").
  // Comprimido es también donde vive el control de abrir el panel, así que hay que
  // saber si se va a renderizar ANTES de decidir dónde poner ese control.
  /**
   * Título con la grilla todavía sin tocar.
   *
   * En Catálogo se dice explícitamente qué pasa con varios elegidos: el modo genera el
   * catálogo COMPLETO de cada producto (vistas × producto), no una foto que los combine.
   * Es la confusión natural con un modo que acepta multi-selección —en "Automática", por
   * ejemplo, varios productos sí salen juntos en una imagen— y es la diferencia entre
   * elegir tres productos esperando tres catálogos o esperando una foto de outfit.
   */
  const tituloSinSeleccion =
    tituloGrilla ??
    (presetKind === 'catalogo-ia' || presetKind === 'catalogo-muebles'
      ? 'Elegí uno o varios productos — armamos el catálogo de cada uno'
      : 'Elegí uno o varios productos');

  const hayFilaRecuento =
    pistasDeUso &&
    !loadingProducts &&
    !mergedEmptyUpload &&
    // En `singleSelect` la fila no va… salvo COMPRIMIDA, donde el texto ya no invita a
    // elegir varios —es sólo el recuento— y es además el único lugar donde entra el
    // control de abrir el panel sin gastar una fila propia
    // (ADR-2026-08-21-video-igual-que-modelo).
    (!singleSelect || compacto);

  // Control de abrir el panel comprimido. Va como ícono y no como texto: el panel
  // mide 280px y una etiqueta al lado del recuento le comería media línea.
  const botonAbrir = onAlternarCompacto ? (
    <button
      type="button"
      onClick={onAlternarCompacto}
      aria-label="Abrir el panel de productos"
      title="Abrir el panel de productos"
      className="shrink-0 grid h-8 w-8 place-items-center rounded-lg text-brand-black/40 hover:bg-black/[0.04] hover:text-brand-black/70 transition-colors"
    >
      <PanelLeftOpen className="w-[18px] h-[18px]" />
    </button>
  ) : null;

  return (
    <div className="flex flex-col h-full">
      {cabecera && <div className={plegarAlScrollear ? collapsibleClass(!headerCollapsed) : 'shrink-0'}>{cabecera}</div>}
      {/* Comprimido el control va EN LA FILA del recuento, a su derecha: en una
          columna de 280px una fila propia para un ícono de 32px costaba ~50px de alto
          que salen de las miniaturas. Sólo cuando esa fila no se renderiza (sin
          productos, cargando, `singleSelect`) el control se queda solo arriba, que es
          el único lugar que queda. */}
      {compacto && !hayFilaRecuento && botonAbrir && (
        <div className="shrink-0 px-6 pt-4">{botonAbrir}</div>
      )}
      {/* Desktop: header fijo. Mobile (`scrollHeader`): header colapsable +
          buscador debajo, ambos afuera del scroll. Si no hay toolbar no tiene
          sentido colapsar nada — no se gana una barra visible a cambio. */}
      {!scrollHeader && headerInner && (
        <div className="shrink-0 px-6">{headerInner}</div>
      )}
      {scrollHeader && headerInner && (
        <div className={`px-6 ${collapsibleClass(!(toolbar && headerCollapsed))}`}>
          {headerInner}
        </div>
      )}
      {scrollHeader && toolbar}

      {/* "tocá otros para sumarlos" decía mal lo que hace: "otros" no tiene antecedente
          —¿otros qué?— y el "los" apunta a lo YA elegido, que es lo contrario de la
          acción. "podés seguir sumando" nombra la acción y deja el objeto implícito.

          Que se puede elegir MÁS DE UNO no se ve en ningún lado hasta que elegís el
          segundo: las tarjetas no tienen checkbox y el bloque de título que lo explicaba
          está escondido en esta pantalla. Con una selección encima pasa a contar cuántos
          van, que es la otra pregunta del momento. No aplica con `singleSelect`, donde
          elegir un segundo reemplaza al primero. */}
      {hayFilaRecuento && plegable(
        // Es el TÍTULO de la pantalla, no un pie de ayuda: con el bloque de "Elegí el
        // producto para generar" escondido, esta línea es lo único que dice qué se
        // espera acá, así que va con el peso de un encabezado.
        <div
          className={`shrink-0 px-6 flex justify-between gap-2 ${
            // Comprimido la fila lleva el botón de abrir, que mide 32px: alineado por
            // baseline colgaría del texto, así que ahí se centra.
            compacto ? 'items-center pt-4 pb-4' : 'items-baseline pt-5 pb-1'
          }`}
        >
        <p className="text-[17px] font-semibold text-brand-black leading-tight">
          {compacto
            ? // En preview la invitación a "tocá otros" sería falsa: los otros no están
              // en pantalla. Queda el recuento a secas.
              `${selectedIds.length} ${
                selectedIds.length === 1 ? 'producto' : 'productos'
              }`
            : selectedIds.length > 0
              ? `${selectedIds.length} ${
                  selectedIds.length === 1
                    ? 'producto elegido'
                    : 'productos elegidos'
                } — podés seguir sumando`
              : tituloSinSeleccion}
        </p>
        {/* Abierto va con texto: ahí el panel es ancho y "Listo" dice qué pasa al
            tocarlo mejor que cualquier ícono. Comprimido, el mismo lugar lo ocupa el
            ícono de abrir. */}
        {onAlternarCompacto &&
          (compacto ? (
            botonAbrir
          ) : (
            <button
              type="button"
              onClick={onAlternarCompacto}
              className="shrink-0 inline-flex items-center gap-0.5 text-brand-primary text-[13px] font-medium hover:opacity-75 transition-opacity"
            >
              Listo
              <Check className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      )}

      {/* Scrollable: (header de upload + botón Subir, modo fusionado) + grilla */}
      {/* Sin `padding-top`: el toolbar de abajo es sticky y un padding acá lo
          ancla por debajo, dejando una franja donde se ven pasar las cards. El
          espacio de arriba lo pone el bloque de subida (`pt-6`) o el propio
          toolbar, que al ser blanco tapa. */}
      <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollRef}
        onScroll={(e) => {
          if (scrollHeader || plegarAlScrollear) handleCollapseOnScroll(e);
          if (pistasDeUso) medirFinDelScroll(e.currentTarget);
        }}
        className={`flex-1 min-h-0 overflow-y-auto px-6${mergedEmptyUpload ? ' flex flex-col justify-center' : ''}`}
        style={espacioAlPie ? { paddingBottom: espacioAlPie } : undefined}
      >
        {/* Cuando hay toolbar, el aire que lo separa del bloque de subida ya lo
            pone su propio `pt-4` (que va adentro de la caja blanca del sticky):
            si además dejáramos el `mb-5`, al no estar scrolleado el buscador
            quedaba flotando lejos del separador "o elegí uno tuyo". */}
        {onStartUpload && (!ocultarBloqueSubida || mergedEmptyUpload) && (
          <div
            className={
              mergedEmptyUpload ? 'pt-6' : showToolbar ? 'pt-6 mb-1' : 'pt-6 mb-5'
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  onStartUpload(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <div className="mb-4">
              <p className="text-[17px] font-semibold leading-tight text-[#16181D]">
                Elegí el producto para generar
              </p>
              <p className="mt-1 text-[13.5px] leading-snug text-black/45">
                {uploadDesc}
              </p>
            </div>
            {/* La tarjeta de subir de Mi marca, con el abanico —
                ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. Con un
                producto ya elegido se achica: deja de ser el paso principal. */}
            <button
              type="button"
              onClick={onStartNewProduct ?? (() => fileInputRef.current?.click())}
              className={`group relative flex w-full items-center overflow-hidden rounded-3xl border-[1.5px] border-dashed border-[#C9D3FF] bg-gradient-to-b from-white to-[#F1F4FF] text-center transition-[border-color,box-shadow,padding] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)] active:scale-[0.99] ${
                hasSelection
                  ? 'flex-row justify-center gap-3 px-4 py-3'
                  : 'flex-col justify-center gap-4 px-5 py-7'
              }`}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
              />
              {hasSelection ? (
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white shadow-[0_4px_10px_-2px_rgba(22,68,235,0.55)]">
                  <Plus className="h-4 w-4" strokeWidth={2.75} />
                </span>
              ) : (
                <AbanicoDeFotos />
              )}
              <span className="relative">
                <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
                  Subir nuevo producto
                </span>
                {!hasSelection && (
                  <span className="mt-1 block text-[13px] leading-snug text-black/45">
                    Cargá sus fotos y empezá a crear
                  </span>
                )}
              </span>
            </button>
            {/* "Sin producto" (ADR-673) — SOLO el video con influencer. Va
                pegado abajo del botón de subir, con menos peso visual: es la
                salida alternativa, no el camino principal. */}
            {onSkipProduct && presetKind === 'video-influencer' && (
              <button
                type="button"
                onClick={onSkipProduct}
                className="w-full mt-2 inline-flex items-center justify-center gap-2.5 py-3 px-6 rounded-2xl text-[14.5px] font-medium text-[#3C3C43]/70 hover:text-brand-black hover:bg-black/[0.03] transition-colors"
              >
                <PackageX className="w-[18px] h-[18px]" />
                Generar sin producto
              </button>
            )}
            {hasProducts && (
              <div className="flex items-center gap-3 mt-5 mb-1">
                <div className="flex-1 h-px bg-black/[0.06]" />
                <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
                  o elegí uno tuyo
                </span>
                <div className="flex-1 h-px bg-black/[0.06]" />
              </div>
            )}
          </div>
        )}
        {/* Toolbar arriba de la grilla (desktop) — en el modo fusionado queda
            debajo del separador "o elegí uno tuyo", que es donde empieza el
            catálogo. Con `scrollHeader` ya se renderizó afuera del scroll. */}
        {!scrollHeader && toolbar}
        {loadingProducts ? (
          <ProductGridSkeleton
            count={6}
            gridClassName={`grid ${gridColsClass} gap-3 pb-3`}
            tileClassName="rounded-2xl"
            withLabel
          />
        ) : noResults ? (
          <ProductCatalogNoResults query={query} onClear={() => setQuery('')} />
        ) : todoElegido ? (
          // La grilla vacía por haber elegido todo NO es el estado vacío del catálogo:
          // sin este cartel se lee como "no tenés productos", justo después de haberlos
          // sumado a todos.
          <div className="py-12 text-center">
            <p className="text-[15px] font-semibold text-brand-black/90">
              Sumaste todos tus productos
            </p>
            <p className="mt-1 text-[13.5px] text-[#3C3C43]/60">
              Están todos en la tanda. Tocá uno abajo para sacarlo.
            </p>
          </div>
        ) : hasSuggestions ? (
          <div className="pb-3">
            {filaDeSubida}
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
              <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                Sugeridos para esta plantilla
              </p>
            </div>
            <div className={`grid ${gridColsClass} gap-3`}>
              {onSubirProducto && renderTileSubir('2xl')}
              {suggested.map((p) => renderTile(p, '2xl'))}
            </div>
            {others.length > 0 && (
              <>
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                  Otros productos
                </p>
                <div className={`grid ${gridColsClass} gap-3`}>
                  {others.map((p) => renderTile(p, '2xl'))}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
          {!compacto && filaDeSubida}
          <div className={`grid ${gridColsClass} gap-3 pb-3`}>
            {onSubirProducto && !compacto && renderTileSubir('2xl')}
            {(compacto
              ? products.filter((p) => selectedIds.includes(p.id))
              : products
            ).map((p) => renderTile(p, '2xl'))}
          </div>
          </>
        )}
      </div>
      {/* Degradado del pie: la última fila se desvanece en vez de cortarse contra el
          borde, que es lo que hace leer la grilla como terminada. Se apaga al llegar
          abajo — si quedara siempre, dejaría de significar "hay más". */}
      {pistasDeUso && quedaContenidoAbajo && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
      )}
      </div>
      {/* EL TABLERO: con el primer producto elegido la tira se muda al lugar que ocupaba
          la muestra del modo, en la columna de al lado, y crece a una grilla
          (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra).
          Colgada del borde del panel la tira mide 76px de ancho: alcanza para decir
          "algo entró", no para VER lo que se está armando. La muestra, en cambio, ya
          cumplió su función en cuanto se eligió el primer producto —explica el modo
          antes de empezar—, así que su espacio es el que sobra en la pantalla y es el
          que se lleva la tanda.
          Va en un portal al nodo que monta el host (no a `body`): así el tablero es un
          hijo NORMAL de esa columna —se acomoda solo, sin medir nada— y al mismo tiempo
          sigue viviendo en el árbol de React de este panel, que es el que tiene la
          selección, el vuelo de la card y el desplegado. */}
      {tiraEnLaMuestra &&
        tiraTablero &&
        destinoTira &&
        ReactDOM.createPortal(
          <div
            ref={tiraRef}
            data-tanda-tablero
            className={`pointer-events-auto w-full max-w-[440px] max-h-full rounded-2xl bg-white shadow-sm ring-1 ring-black/[0.05] p-3 animate-mode-swap${
              // Desplegado crece hacia abajo en filas de a tres: con tope de alto y
              // scroll, para que el tablero no se pase del alto de la columna.
              tiraTablero.expandida ? ' overflow-y-auto overflow-x-hidden' : ''
            }`}
          >
            {/* Tres columnas y no seis: las celdas son cuadradas y a seis por fila, en
                440px, volverían a medir lo que medían colgadas del panel. De a tres, la
                miniatura se ve —que es lo que se ganó al mudarse acá—. */}
            <div className="grid grid-cols-3 gap-3">
              {contenidoTira({
                tira: tiraTablero,
                chevronAbajo: true,
                conHuecos: true,
                prefijoKey: 'tablero',
              })}
            </div>
          </div>,
          destinoTira,
        )}

      {/* Ajustes al pie. Van FUERA del área scrolleable —y antes del footer— para que
          no haya que scrollear la grilla hasta el final para encontrarlos. En el estado
          vacío no se muestran: ahí todavía no hay nada que configurar. */}
      {pie && !mergedEmptyUpload && <div className="shrink-0">{pie}</div>}

      {/* Footer con Confirmar. Se oculta en el modo fusionado SIN productos:
          ahí el único paso lógico es subir, no hay nada que confirmar. En el
          modo fusionado CON productos el footer aparece (con un slide-up sutil)
          recién cuando hay una selección — antes el CTA es subir/elegir. */}
      {!hideFooter &&
        !mergedEmptyUpload &&
        (footerSiempreVisible || !onStartUpload || hasSelection) && (
          <div
            className={`shrink-0 px-6 pt-3 pb-4 bg-white${
              footerSinBorde ? '' : ' border-t border-black/[0.05]'
            }${
              // La animación de entrada es sólo para el footer que APARECE. Con
              // `footerSiempreVisible` no aparece nunca —está desde el principio— y el
              // slide-up lo dispararía cada vez que re-renderiza.
              onStartUpload && !footerSiempreVisible ? ' animate-mode-swap' : ''
            }`}
          >
            {tira &&
              (!tiraALaDerecha || tiraAlPieDeRespaldo) &&
              (!esMobileReal || tira.visibles.length > 0) && (
              // Al PIE (mobile): fila de 6 celdas. En desktop la tira se fue a la
              // derecha, en columna (ADR-2026-09-04-la-tira-de-elegidos-va-a-la-derecha).
              //
              // GRID de 6 columnas y no un flex con `flex-1`: repartiendo por flex, las
              // celdas con foto (`<button>` con imagen adentro) y las vacías (`<span>`)
              // resolvían su ancho distinto y la fila salía despareja —77px las llenas
              // contra 80px las vacías, visible como un salto en el medio—. `grid-cols-6`
              // fija las columnas antes de mirar el contenido, así que todas miden igual
              // esté la tira vacía, llena o a medias. Las columnas son 6 porque
              // `TIRA_CELDAS` es 6: si cambia una, cambia la otra.
              // Desplegada crece hacia arriba en filas de a 6, con tope de alto: dos
              // filas y media a la vista y el resto por scroll, para que la tira nunca
              // se coma la grilla de productos que está justo arriba.
              <div
                ref={tiraRef}
                className={`mb-3 grid grid-cols-6 gap-2${
                  tira.expandida ? ' max-h-[196px] overflow-y-auto pr-0.5' : ''
                }`}
              >
                {contenidoTira({
                  tira,
                  // La fila del pie crece hacia ARRIBA: el chevron de desplegar apunta
                  // para allá.
                  chevronAbajo: false,
                  // Los huecos punteados NO van en el teléfono
                  // (ADR-2026-09-04-en-mobile-el-panel-no-dibuja-la-tira-de-elegidos):
                  // ahí la fila arranca vacía —seis recuadros grises antes de elegir
                  // nada— y se lee como celdas rotas, no como "acá puede entrar más".
                  conHuecos: !esMobileReal,
                  prefijoKey: 'pie',
                })}
              </div>
            )}
            {confirmHint && (
              <p
                className={`text-center text-[12.5px] leading-snug ${
                  confirmHintDestacado
                    ? 'mb-4 text-brand-black/90'
                    : 'mb-2 text-[#3C3C43]/55'
                }`}
              >
                {confirmHint}
              </p>
            )}
            <div className="flex items-center gap-2">
              {/* `[&>span]:mt-0`: `ContadorImagenes` trae un `mt-1` propio —viene de
                  cuando vivía debajo de la etiqueta de una celda— y acá, en una caja
                  centrada, esos 4px lo descuelgan del botón de al lado. Se neutraliza
                  desde afuera para no tocar el componente, que en sus otros usos sí
                  necesita ese aire. */}
              {antesDelConfirm && (
                <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 self-stretch flex items-center [&>span]:mt-0">
                  {antesDelConfirm}
                </div>
              )}
              <button
                type="button"
                onClick={onConfirmOverride ?? handleConfirm}
                disabled={selectedIds.length === 0 || confirmDisabled}
                className="flex-1 min-w-0 inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
              >
                <span className="truncate">{confirmLabel}</span>
                {confirmBadge}
                {/* Sin la pastilla con la cantidad cuando el título ya la dice ("3
                    productos elegidos"): eran dos lugares informando lo mismo a diez
                    centímetros uno del otro. Donde no hay título —el resto de los
                    modos— la pastilla sigue siendo la única señal y se mantiene. */}
                {!confirmBadge && !pistasDeUso && selectedIds.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white/25 text-[12px] font-bold leading-none">
                    {selectedIds.length}
                  </span>
                )}
              </button>
            </div>
            {confirmNota && (
              <p className="mt-2.5 text-center text-[12.5px] text-brand-black/45">
                {confirmNota}
              </p>
            )}
          </div>
        )}
      {editModal}
    </div>
  );
};
